"use client";

import {
	type ReactNode,
	type RefObject,
	useCallback,
	useMemo,
	useRef,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib";
import { PictureInPictureResizeHandles } from "./PictureInPictureResizeHandles";
import type { PipSnapConfig } from "./pip-config";
import { DEFAULT_PIP_SNAP_CONFIG } from "./pip-config";
import { scalePipDimensions } from "./pip-resize";
import type { PipResizeConfig, PipResizeCorner } from "./pip-resize-config";
import { resolvePipResizeConfigFromProp } from "./pip-resize-config";
import type { PipDimensions, PipPosition, PipSnapInsets } from "./pip-snap";
import { toPortalPosition } from "./pip-stage-coordinates";
import { useFloatingPipDrag } from "./use-floating-pip-drag";
import { useFloatingPipResize } from "./use-floating-pip-resize";
import { usePipCoordinateBridge } from "./use-pip-coordinate-bridge";
import { usePipMeasuredDimensions } from "./use-pip-measured-dimensions";
import { usePipPortalHost } from "./use-pip-portal-host";

export interface FloatingPipDragContext {
	isDragging: boolean;
	isResizing?: boolean;
	scale?: number;
	onPointerDown: React.PointerEventHandler<HTMLElement>;
	onPointerMove: React.PointerEventHandler<HTMLElement>;
	onPointerUp: React.PointerEventHandler<HTMLElement>;
	onResizePointerDown?: (
		corner: PipResizeCorner,
	) => React.PointerEventHandler<HTMLElement>;
	onResizePointerMove?: React.PointerEventHandler<HTMLElement>;
	onResizePointerUp?: React.PointerEventHandler<HTMLElement>;
}

export interface FloatingPictureInPictureProps {
	/** Bounds stage for clamp/snap math. */
	stageRef: RefObject<HTMLElement | null>;
	/** Optional portal target when it should differ from {@link stageRef}. */
	portalStageRef?: RefObject<HTMLElement | null>;
	enabled?: boolean;
	/** Fixed dimensions — used when the PiP size is known in px (e.g. live call). */
	dimensions?: PipDimensions;
	/** When set, dimensions are measured from this element (e.g. responsive vw/vh PiP). */
	measureDimensions?: boolean;
	config?: PipSnapConfig;
	/** Extra clamp/snap padding merged on top of document safe-area insets. */
	snapInsets?: PipSnapInsets;
	/** Optional scale resize with corner handles. */
	resize?: PipResizeConfig | boolean;
	/** Mount the overlay inside the portal target (defaults to {@link stageRef}). */
	portalToStage?: boolean;
	className?: string;
	overlayClassName?: string;
	pipClassName?: string;
	testId?: string;
	children: ReactNode | ((drag: FloatingPipDragContext) => ReactNode);
}

const DEFAULT_FALLBACK_DIMENSIONS: PipDimensions = {
	widthPx: 130,
	heightPx: 130,
};

export function FloatingPictureInPicture({
	stageRef,
	portalStageRef,
	enabled = true,
	dimensions,
	measureDimensions = false,
	config = DEFAULT_PIP_SNAP_CONFIG,
	snapInsets,
	resize,
	portalToStage = false,
	className,
	overlayClassName,
	pipClassName,
	testId,
	children,
}: FloatingPictureInPictureProps) {
	const pipRef = useRef<HTMLDivElement>(null);
	const livePositionRef = useRef<PipPosition>({ x: 0, y: 0 });
	const resizeConfig = resolvePipResizeConfigFromProp(resize);
	const resizeEnabled = resizeConfig != null;
	const portalTargetRef = portalStageRef ?? stageRef;
	const coordinateBridgeActive =
		portalToStage && portalStageRef != null && portalStageRef !== stageRef;
	const coordinateOffset = usePipCoordinateBridge(
		stageRef,
		portalStageRef,
		coordinateBridgeActive,
	);
	const portalHost = usePipPortalHost(portalTargetRef, portalToStage);
	const fallbackDimensions = dimensions ?? DEFAULT_FALLBACK_DIMENSIONS;
	const measured = usePipMeasuredDimensions(pipRef, fallbackDimensions, {
		// Only lock a CSS-measured base when resize depends on it; fixed-dimension
		// PiPs (e.g. Ask Live) do not need this gate.
		lockBase: resizeEnabled && measureDimensions,
		// Portal host often attaches after the first layout pass — remeasure then
		// so corner resize handles are not stuck waiting forever for isBaseLocked.
		observeKey: portalToStage ? portalHost : true,
	});
	const baseDimensions = measureDimensions
		? measured.dimensions
		: fallbackDimensions;
	const isResizeBaseLocked =
		!resizeEnabled || !measureDimensions || measured.isBaseLocked;

	const dragSetPositionRef = useRef<(position: PipPosition) => void>(
		() => {},
	);

	const handleResizeLayout = useCallback(
		(layout: {
			scale: number;
			position: PipPosition;
			dimensions: PipDimensions;
		}) => {
			dragSetPositionRef.current(layout.position);
		},
		[],
	);

	const pipResize = useFloatingPipResize({
		enabled: resizeEnabled && isResizeBaseLocked,
		boundsStageRef: stageRef,
		baseDimensions,
		config: resizeConfig ?? {
			minScale: 1,
			maxScale: 1,
			snapScales: [1],
			snapThresholdScale: 0,
			snapTransitionMs: config.snapTransitionMs,
			cornerHitSizePx: 14,
		},
		snapConfig: config,
		snapInsets,
		positionRef: livePositionRef,
		onLayoutChange: handleResizeLayout,
	});

	const activeDimensions = useMemo(
		() =>
			resizeEnabled
				? scalePipDimensions(baseDimensions, pipResize.scale)
				: baseDimensions,
		[
			baseDimensions.heightPx,
			baseDimensions.widthPx,
			pipResize.scale,
			resizeEnabled,
		],
	);
	const dragPipDimensions = useMemo(
		() => (isResizeBaseLocked ? activeDimensions : fallbackDimensions),
		[
			activeDimensions.heightPx,
			activeDimensions.widthPx,
			fallbackDimensions.heightPx,
			fallbackDimensions.widthPx,
			isResizeBaseLocked,
		],
	);

	const drag = useFloatingPipDrag({
		enabled,
		stageRef,
		pipDimensions: dragPipDimensions,
		config,
		snapInsets,
	});

	dragSetPositionRef.current = drag.setPosition;
	livePositionRef.current = drag.position;

	const useExplicitDimensions = resizeEnabled
		? isResizeBaseLocked
		: !measureDimensions;
	const isInteracting = drag.isDragging || pipResize.isResizing;
	const renderPosition = useMemo(
		() =>
			coordinateBridgeActive
				? toPortalPosition(drag.position, coordinateOffset)
				: drag.position,
		[
			coordinateBridgeActive,
			coordinateOffset.x,
			coordinateOffset.y,
			drag.position.x,
			drag.position.y,
		],
	);

	if (!enabled) {
		return null;
	}

	if (portalToStage && portalHost == null) {
		return null;
	}

	const dragContext: FloatingPipDragContext = {
		isDragging: drag.isDragging,
		onPointerDown: drag.onPointerDown,
		onPointerMove: drag.onPointerMove,
		onPointerUp: drag.onPointerUp,
		scale: resizeEnabled ? pipResize.scale : undefined,
		isResizing: resizeEnabled ? pipResize.isResizing : undefined,
		onResizePointerDown: resizeEnabled
			? pipResize.onResizePointerDown
			: undefined,
		onResizePointerMove: resizeEnabled
			? pipResize.onResizePointerMove
			: undefined,
		onResizePointerUp: resizeEnabled
			? pipResize.onResizePointerUp
			: undefined,
	};

	const resolvedChildren =
		typeof children === "function" ? children(dragContext) : children;

	const overlay = (
		<div
			className={cn(
				"pointer-events-none absolute inset-0 overflow-visible",
				overlayClassName,
			)}
			data-testid={testId}
			onPointerMove={
				resizeEnabled ? pipResize.onResizePointerMove : undefined
			}
			onPointerUp={
				resizeEnabled ? pipResize.onResizePointerUp : undefined
			}
		>
			<div
				ref={pipRef}
				className={cn(
					"pointer-events-auto absolute z-20",
					pipClassName,
					className,
				)}
				style={{
					left: renderPosition.x,
					top: renderPosition.y,
					width: useExplicitDimensions
						? activeDimensions.widthPx
						: undefined,
					height: useExplicitDimensions
						? activeDimensions.heightPx
						: undefined,
					transition: isInteracting
						? undefined
						: `left ${config.snapTransitionMs}ms ease-out, top ${config.snapTransitionMs}ms ease-out, width ${resizeConfig?.snapTransitionMs ?? config.snapTransitionMs}ms ease-out, height ${resizeConfig?.snapTransitionMs ?? config.snapTransitionMs}ms ease-out`,
				}}
			>
				{resolvedChildren}
				{resizeEnabled && isResizeBaseLocked ? (
					<PictureInPictureResizeHandles
						hitSizePx={resizeConfig.cornerHitSizePx}
						onResizePointerDown={pipResize.onResizePointerDown}
						onResizeDoubleClick={pipResize.onResizeDoubleClick}
					/>
				) : null}
			</div>
		</div>
	);

	if (portalToStage && portalHost != null) {
		return createPortal(overlay, portalHost);
	}

	return overlay;
}
